import { createComponentStore } from '../../utils/createComponentStore';
import { days } from '../../utils/Days';

const pad2 = (n) => String(n).padStart(2, '0');

const formatDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const formatTime = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

// Java LocalDate: { year, month, day } → "YYYY-MM-DD"
const normalizeDate = (val) => {
  if (!val || typeof val === 'string') return val || '';
  if (val.year != null) return `${val.year}-${pad2(val.month)}-${pad2(val.day)}`;
  return '';
};

// Java LocalTime: { hour, minute } → "HH:MM"
const normalizeTime = (val) => {
  if (!val || typeof val === 'string') return val || '';
  if (val.hour != null) return `${pad2(val.hour)}:${pad2(val.minute)}`;
  return '';
};

const localTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
};

const buildComponentData = () => {
  const now = new Date();
  const tz = localTimezone();
  return {
    outputKey: 'schedule' + Math.floor(Math.random() * 90000000 + 10000),
    mode: 'specific_date_time',
    date: formatDate(now),
    time: formatTime(now),
    selectDays: days,
    repeat: { interval: 5, type: 'minute' },
    stopCondition: {
      times: 1,
      type: 'after_a_certain_number_of_repeats',
      date: formatDate(now),
      time: formatTime(now),
      timezone: tz,
    },
    timezone: tz,
  };
};

const { createStore, getStore, defaultStore } = createComponentStore({
  type: 'schedule',
  endpoint: 'schedule-start',
  registryKey: 'schedule-start',
  initData: buildComponentData,

  onLoadData: (rawData) => {
    const data = { ...rawData };
    // v1 compat mappings
    if (data.startWorkflow && !data.mode) { data.mode = data.startWorkflow; delete data.startWorkflow; }
    if (data.stopRunning && !data.stopCondition) { data.stopCondition = data.stopRunning; delete data.stopRunning; }
    if (data.key && !data.outputKey) { data.outputKey = data.key; delete data.key; }
    data.date = normalizeDate(data.date);
    data.time = normalizeTime(data.time);
    if (data.stopCondition) {
      data.stopCondition = {
        ...data.stopCondition,
        date: normalizeDate(data.stopCondition.date),
        time: normalizeTime(data.stopCondition.time),
      };
    }
    return data;
  },

  extraState: () => ({
    position: null,
    error: false,
    json: undefined,
  }),

  extraActions: (set, get) => ({
    setScheduleStartBody: (body) => set({ json: body }),

    setScheduleStartName: (name) => set({ name }),

    updateData: (patch) => set((s) => ({ data: { ...s.data, ...patch } })),

    setContent: (value, type) => {
      set((s) => {
        const data = { ...s.data };
        switch (type) {
          case 'key': data.outputKey = value; break;
          case 'workflow': data.mode = value; break;
          case 'date': data.date = value; break;
          case 'time': data.time = value; break;
          case 'selectDays': {
            const selectDays = (data.selectDays || days).map((d) =>
              d.key === String(value) ? { ...d, active: !d.active } : d
            );
            data.selectDays = selectDays;
            break;
          }
        }
        return { data };
      });
    },

    setStopContent: (value, type) => {
      set((s) => ({
        data: {
          ...s.data,
          stopCondition: { ...s.data.stopCondition, [type]: value },
        },
      }));
    },

    setRepeat: (value, type) => {
      const field = type === 'interval' ? 'interval' : 'type';
      set((s) => ({
        data: {
          ...s.data,
          repeat: { ...s.data.repeat, [field]: value },
        },
      }));
    },

    setStopRunning: (value, type) => {
      const field = type === 'times' ? 'times' : 'type';
      set((s) => ({
        data: {
          ...s.data,
          stopCondition: { ...s.data.stopCondition, [field]: value },
        },
      }));
    },

    getTemplate: () => {
      const { componentId, name, position, data, type, lastModified } = get();
      return { componentId, name, position, data: structuredClone(data), type, lastModified };
    },
  }),
});

export { normalizeDate, normalizeTime };

export function getScheduleStartStore(componentId) {
  return getStore(componentId);
}

export default defaultStore;
